import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type LanguageContextType = {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  getLocalizedPath: (path: string) => string;
};

// Map of path translations for each language
const pathTranslations: Record<string, Record<string, string>> = {
  de: {
    blog: 'blog',
    services: 'leistungen',
    about: 'ueber-uns',
    contact: 'kontakt',
    privacy: 'datenschutz',
    legal: 'impressum',
    terms: 'agb',
  },
  en: {
    blog: 'blog',
    services: 'services',
    about: 'about',
    contact: 'contact',
    privacy: 'privacy-policy',
    legal: 'legal',
    terms: 'terms',
  },
  fr: {
    blog: 'blog',
    services: 'services',
    about: 'a-propos',
    contact: 'contact',
    privacy: 'politique-de-confidentialite',
    legal: 'mentions-legales',
    terms: 'conditions',
  },
  it: {
    blog: 'blog',
    services: 'servizi',
    about: 'chi-siamo',
    contact: 'contatto',
    privacy: 'informativa-privacy',
    legal: 'note-legali',
    terms: 'termini',
  },
};

// Get the standard key for a localized path
const getStandardKeyFromPath = (lang: string, path: string): string | null => {
  const translations = pathTranslations[lang];
  if (!translations) return null;
  
  for (const [key, translatedPath] of Object.entries(translations)) {
    if (path === translatedPath) return key;
  }
  
  return null;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  
  // Use the URL language param if available, otherwise use from localStorage, default to 'de'
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (lang && ['de', 'en', 'fr', 'it'].includes(lang)) {
      return lang;
    }
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return (savedLanguage && ['de', 'en', 'fr', 'it'].includes(savedLanguage)) ? savedLanguage : 'de';
  });

  // Update localStorage and HTML lang attribute when language changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', currentLanguage);
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Update currentLanguage if URL language parameter changes
  useEffect(() => {
    if (lang && ['de', 'en', 'fr', 'it'].includes(lang) && lang !== currentLanguage) {
      setCurrentLanguage(lang);
    }
  }, [lang]);

  const setLanguage = (language: string) => {
    if (['de', 'en', 'fr', 'it'].includes(language)) {
      setCurrentLanguage(language);
      
      // Update URL to reflect language change
      const currentPath = window.location.pathname;
      const pathParts = currentPath.split('/').filter(Boolean);
      
      if (pathParts.length > 0) {
        const currentLang = pathParts[0];
        if (['de', 'en', 'fr', 'it'].includes(currentLang)) {
          // We're already on a language path, update it
          const remainingPath = pathParts.slice(1).join('/');
          const standardKey = remainingPath ? getStandardKeyFromPath(currentLang, pathParts[1]) : null;
          
          if (standardKey) {
            // We can map this path to the new language
            const newPath = `/${language}/${pathTranslations[language][standardKey]}`;
            if (pathParts.length > 2) {
              // Preserve remaining path segments (e.g., blog post slugs)
              navigate(`${newPath}/${pathParts.slice(2).join('/')}`);
            } else {
              navigate(newPath);
            }
          } else {
            // Can't map the path, just change the language prefix
            navigate(`/${language}`);
          }
        } else {
          // No language in the path, add it
          navigate(`/${language}/${pathParts.join('/')}`);
        }
      } else {
        // Root path, just add language
        navigate(`/${language}`);
      }
    }
  };

  // Helper function to get localized paths
  const getLocalizedPath = (key: string): string => {
    const translations = pathTranslations[currentLanguage];
    if (translations && translations[key]) {
      return `/${currentLanguage}/${translations[key]}`;
    }
    return `/${currentLanguage}`;
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
